import React, {createContext, PropsWithChildren, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {DocumentRead, TagRead, Group, JobType, JobStatus} from "../../schemas"; // ⬅️ for filtering job types
import {useNavigate} from "react-router-dom";
import {useEnhancedDocumentsContext} from "../doc_contexts";
import {useGroupContext} from "../group_context";
import {useFolderContext} from "../folder_context";
import {MdKeyboardArrowRight} from 'react-icons/md';
import SplitNavSelector from '../../components/SplitNavSelector';
import {useUserContext} from "../user_context";
import {EnableGroupView} from "../../feature_flags";
import {AppView, AppViewToLinkMap} from '../../App';
import {getValidAccessToken} from "../../api";
import {useJobContext} from "../job_context";
import {CustomFlow} from "./special-flow-info";
import {setGlobalNavigate} from "./nav-service"; // ⬅️ jobs context

export enum GridView {
    Default = "DEFAULT",
    MyDashboard = "MY_DASHBOARD",
}

export interface NavigationState {
    view: AppView; // List of views to track
    doc?: DocumentRead | null;
    group?: Group | null;
    folder?: TagRead | null;  // Folder is now a TagRead with type=FOLDER_UPLOAD
    link?: string; // If defined, navigate to this link, ignore other fields
}

export interface NavigationProviderContextType {
    navigateTo: (newView: NavigationState) => void;
    customFlow: CustomFlow;
    changeDocView: (doc: DocumentRead) => void;
    goBack: () => void;
    currentView: AppView | undefined;
    navLoading: boolean;
    setNavLoading: (loading: boolean) => void;

    docSelectedListenersActive?: boolean; // Whether doc selected listeners are active
    setDocSelectedListenersActive: (active: boolean) => void; // Function to set the above

    setDocumentAsync: (doc: DocumentRead | null | undefined) => Promise<DocumentRead | null | undefined>;
    setCurrentFolderAsync: (folder: TagRead | null | undefined) => Promise<TagRead | null | undefined>;
    setCurrentGroupAsync: (group: Group | null | undefined) => Promise<Group | null | undefined>;

    getNavHeader: () => ReactNode;

    JobsBlocker: JobsBlockerComponent;

    isDocExpanded: boolean;
    setIsDocExpanded: (expanded: boolean) => void;
}

type JobsBlockFilter = {
    onTagJobs?: boolean;             // block if any JobType.TAG_UPDATE active
    onUploadJobs?: boolean;          // block if any of uploadTypes active
    extraTypes?: JobType[];          // any other job types to block on
    message?: string;                // overlay title
    lines?: string[];                // optional sub-lines
    blur?: boolean;                  // subtle blur behind overlay (default: true)
    className?: string;              // wrapper class
};

type JobsBlockerComponent = React.FC<PropsWithChildren<JobsBlockFilter>>;


export const NavigationProviderContext = createContext<NavigationProviderContextType | undefined>(undefined);

interface NavigationProviderProps {
    children: ReactNode;
}

export const NavigationProvider: React.FC<NavigationProviderProps> = ({children}) => {
    const navigate = useNavigate();

    useEffect(() => {
        setGlobalNavigate(navigate);
    }, [navigate]);
    const [docSelectedListenersActive, setDocSelectedListenersActive] = useState(false);

    const {setDocument, setLastVisitedDocument} = useEnhancedDocumentsContext()!;
    const [customFlow, setCustomFlow] = useState<CustomFlow>({firstTimeFlow: false});
    const {setCurrentFolder} = useFolderContext()!;
    const {currentUser} = useUserContext()!;
    const {getCurrentGroup, setCurrentGroup} = useGroupContext()!;
    const [currentView, setCurrentView] = useState<AppView | undefined>(undefined);
    const [navigationStack] = useState<NavigationState[]>([]);
    const [navLoading, setNavLoading] = useState(false);
    const [isDocExpanded, setIsDocExpanded] = useState(false);

    useEffect(() => {
        setCustomFlow({firstTimeFlow: !currentUser?.user_metadata.first_time_flow_completed});
    }, [currentUser]);

    // Remove direct usage of window.history; instead, rely on useNavigate.
    const _updateUrlFromNavState = (navState: NavigationState): string => {
        let targetUrl = AppViewToLinkMap[navState.view];
        if (navState.view === AppView.FolderView && navState.group) {
            targetUrl = targetUrl.replace(':groupId', navState.group.id.toString());
        } else if (navState.view === AppView.DocumentSearch && navState.group && navState.folder) {
            targetUrl = targetUrl.replace(':groupId', navState.group.id.toString());
            targetUrl = targetUrl.replace(':folderId', navState.folder.id.toString());
        } else if (navState.view === AppView.SingleDocumentView && navState.group && navState.folder && navState.doc) {
            targetUrl = targetUrl.replace(':groupId', navState.group.id.toString());
            targetUrl = targetUrl.replace(':folderId', navState.folder.id.toString());
            targetUrl = targetUrl.replace(':docId', navState.doc.id.toString());
        }
        if (targetUrl) {
            // Use React Router's navigate instead of directly calling window.history.pushState.
            navigate(targetUrl);
        }
        return targetUrl;
    };

    async function setCurrentFolderAsync(folder: TagRead | undefined | null): Promise<TagRead | undefined | null> {
        setNavLoading(true);
        return new Promise((resolve) => {
            setTimeout(() => {
                setCurrentFolder(folder);
                resolve(folder);
                setNavLoading(false);
            }, 100);
        });
    }

    async function setDocumentAsync(doc: DocumentRead | undefined | null): Promise<DocumentRead | undefined | null> {
        setNavLoading(true);
        return new Promise((resolve) => {
            setTimeout(() => {
                setDocument(doc);
                resolve(doc);
                setNavLoading(false);
            }, 100);
        });
    }

    async function setCurrentGroupAsync(group: Group | undefined | null): Promise<Group | undefined | null> {
        setNavLoading(true);
        return new Promise((resolve) => {
            setTimeout(() => {
                setCurrentGroup(group);
                resolve(group);
                setNavLoading(false);
            }, 100);
        });
    }

    const navigateTo = async (newView: NavigationState) => {
        // TODO we might want to reset contexts to save memory
        if (navigationStack.length > 0 && navigationStack[navigationStack.length - 1].doc) {
            setLastVisitedDocument(navigationStack[navigationStack.length - 1].doc!);
        }
        navigationStack.push(newView);

        const fallback = async () => {
            setCurrentView(AppView.MyDashboard);
            navigate(AppViewToLinkMap[AppView.MyDashboard]);
        }
        if (!getValidAccessToken()) {
            navigate(AppViewToLinkMap[AppView.Login]);
            return;
        }
        if (newView.view == AppView.Login) {
            navigate(AppViewToLinkMap[AppView.Login]);
            return;
        }

        const currentGroup = await getCurrentGroup();
        if (!EnableGroupView && newView.view == AppView.MyDashboard) {
            newView.view = AppView.FolderView;
            newView.group = currentGroup;
        }
        if (newView.view === AppView.MyDashboard) {
            await setCurrentGroupAsync(null);
            if (currentUser === null) {
                navigate(AppViewToLinkMap[AppView.Login]);
                return;
            }
        }

        if (newView.view === AppView.FolderView) {
            if (newView.group) {
                await setCurrentGroupAsync(newView.group);
                await setCurrentFolderAsync(null);
            } else {
                await setCurrentGroupAsync(null);
                await fallback();
                return;
            }
        }
        if (newView.view === AppView.DocumentSearch) {
            if (newView.folder && newView.group) {
                await setCurrentGroupAsync(newView.group);
                await setCurrentFolderAsync(newView.folder);
            } else {
                await setCurrentGroupAsync(null);
                await setCurrentFolderAsync(null);
                await fallback();
                return;
            }
        }
        if (newView.view === AppView.SingleDocumentView) {
            if (newView.doc && newView.group && newView.folder) {
                await setCurrentGroupAsync(newView.group);
                await setCurrentFolderAsync(newView.folder);
            } else {
                await setCurrentGroupAsync(null);
                await setCurrentFolderAsync(null);
                await fallback();
                return;
            }
        }
        setCurrentView(newView.view);
        // For views that require URL parameters, _updateUrlFromNavState builds the URL and navigates.
        // For others, directly navigate using the link map.
        const shouldHaveParams = [AppView.SingleDocumentView, AppView.DocumentSearch, AppView.FolderView].includes(newView.view);
        if (!shouldHaveParams) {
            navigate(AppViewToLinkMap[newView.view]);
        } else {
            _updateUrlFromNavState(newView);
        }
    };

    const changeDocView = (doc: DocumentRead) => {
        if (navigationStack.length > 0) {
            navigationStack[navigationStack.length - 1] = {
                ...navigationStack[navigationStack.length - 1],
                doc: doc
            };
            setDocument(doc);
            // Update URL to include docId — append after /workspace/groupId/folderId
            // Count existing UUID segments to know if a docId is already present
            const currentPath = window.location.pathname;
            const uuids = currentPath.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/g) || [];
            if (uuids.length >= 3) {
                // Already has a docId — replace the last UUID
                const lastUuid = uuids[uuids.length - 1];
                const basePath = currentPath.slice(0, currentPath.lastIndexOf(lastUuid) - 1);
                window.history.replaceState(null, '', `${basePath}/${doc.id}`);
            } else {
                // No docId yet — append
                window.history.replaceState(null, '', `${currentPath}/${doc.id}`);
            }
        }
    };

    const goBack = () => {
        if (navigationStack.length > 1) {
            const oldView: NavigationState = navigationStack.pop()!;
            if (oldView.doc) {
                setLastVisitedDocument(oldView.doc);
            }
        }
        if (navigationStack.length > 0) {
            const lastView = navigationStack[navigationStack.length - 1];
            _updateUrlFromNavState(lastView);
            setCurrentView(lastView.view);
            setDocumentAsync(lastView.doc);
            setCurrentGroupAsync(lastView.group);
        } else {
            navigate(-1); // Use React Router's go back
        }
    };

    // Pull hook calls out so getNavHeader can be memoized with useCallback
    const {currentGroup: navGroup, userGroups} = useGroupContext()!;
    const {currentFolder: navFolder, groupFolders} = useFolderContext()!;
    const {document: navDoc} = useEnhancedDocumentsContext()!;

    const navigateToRef = useRef(navigateTo);
    navigateToRef.current = navigateTo;

    const getNavHeader = useCallback(() => {
        if (navigationStack.length === 0) {
            return null;
        }

        return (
            <div className="flex pl-2 ">
                {/* Group Column */}
                {navGroup && EnableGroupView && (
                    <SplitNavSelector
                        label="Groups"
                        currentName={navGroup.name}
                        items={(userGroups ?? []).map(g => ({id: g.id, name: g.name, count: g.document_count}))}
                        activeId={navGroup.id}
                        onSelect={(item) => {
                            const group = userGroups?.find(g => g.id === item.id);
                            if (group) navigateToRef.current({view: AppView.FolderView, group});
                        }}
                        onBack={() => navigateToRef.current({view: AppView.MyDashboard})}
                        dropdownLabel="Switch group"
                    />
                )}

                {/* Folder Column */}
                {navFolder && (
                    <SplitNavSelector
                        label="Folders"
                        currentName={navFolder.tag_name}
                        items={(groupFolders ?? []).map(f => ({id: f.id, name: f.tag_name, count: f.document_count}))}
                        activeId={navFolder.id}
                        onSelect={(item) => {
                            const folder = groupFolders?.find(f => f.id === item.id);
                            if (folder) navigateToRef.current({view: AppView.DocumentSearch, folder, group: navGroup!});
                        }}
                        onBack={() => navigateToRef.current({view: AppView.FolderView, group: navGroup!})}
                        showArrow={!!(navGroup && EnableGroupView)}
                    />
                )}

                {/* Document Column */}
                {navDoc && (
                    <div className="flex flex-col items-start">
                        <span className="text-xs text-gray-500 mb-1">Documents</span>
                        <div
                            className="flex items-center space-x-2 cursor-pointer group hover:opacity-75"
                            onClick={() =>
                                navigateToRef.current({
                                    view: AppView.DocumentSearch,
                                    group: navGroup,
                                    folder: navFolder,
                                })
                            }
                        >
                            <MdKeyboardArrowRight className="text-lg text-gray-600 group-hover:text-blue-600"/>
                            <span className="font-semibold text-md text-gray-800 group-hover:text-blue-600">
              {navDoc.id}
            </span>
                        </div>
                    </div>
                )}
            </div>
        );
    }, [navGroup, navFolder, navDoc, navigationStack.length, groupFolders, userGroups]);

    // Memoize JobsBlocker so its component reference stays stable across
    // NavigationProvider re-renders. A changing reference would cause React
    // to unmount/remount all children (destroying Slate editors, etc.).
    const JobsBlocker: JobsBlockerComponent = useMemo(() => {
        const Component: JobsBlockerComponent = ({
                                                     children,
                                                     onTagJobs = false,
                                                     onUploadJobs = false,
                                                     extraTypes = [],
                                                     message = "Working… Please wait",
                                                     lines,
                                                     blur = true,
                                                     className = "",
                                                 }) => {
            const {activeJobs} = useJobContext()!;

            const shouldBlock = useMemo(() => {
                if (!Array.isArray(activeJobs) || activeJobs.length === 0) return false;
                const match = (t: JobType) => {
                    if (onTagJobs && t === JobType.TAG_UPDATE) return true;
                    if (onUploadJobs && t === JobType.UPLOAD) return true;
                    if (extraTypes.includes(t)) return true;
                    return false;
                };
                // Only block on jobs that are actually in progress — not completed/failed with warnings
                return activeJobs.some(j =>
                    j?.type && match(j.type) &&
                    j.status !== JobStatus.COMPLETED && j.status !== JobStatus.FAILED
                );
            }, [activeJobs, onTagJobs, onUploadJobs, extraTypes]);

            const computedLines: string[] | undefined = useMemo(() => {
                if (lines && lines.length) return lines;
                const l: string[] = [];
                if (onUploadJobs) l.push("Uploading / ingesting…");
                if (onTagJobs) l.push("Tagging & processing…");
                if (extraTypes.length) l.push("Background tasks running…");
                return l.length ? l : undefined;
            }, [lines, onUploadJobs, onTagJobs, extraTypes.length]);

            return (
                <div
                    className={`relative flex flex-col h-full w-full ${className}`}
                    aria-busy={shouldBlock}
                    aria-disabled={shouldBlock}
                >
                    <div
                        className={`flex-1 min-h-0 flex flex-col ${shouldBlock && blur ? "blur-[1px]" : ""}`}
                        aria-hidden={shouldBlock}
                    >
                        {children}
                    </div>

                    {shouldBlock && (
                        <div
                            className="absolute inset-0 z-50 flex items-center justify-center bg-white/70 backdrop-blur-sm overflow-hidden"
                            role="alert"
                            aria-live="polite"
                            aria-modal="true"
                        >
                            <div
                                className="flex flex-col items-center gap-3 px-6 py-4 rounded-2xl bg-white/80 shadow-md pointer-events-auto">
                                <div
                                    className="h-8 w-8 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin"/>
                                <div className="text-sm text-gray-700 font-medium">{message}</div>
                                {computedLines && (
                                    <ul className="text-xs text-gray-500 ">
                                        {computedLines.map((l, i) => (
                                            <li key={i} className="flex items-center gap-2">
                                                <span
                                                    className="h-1.5 w-1.5 rounded-full bg-gray-400 inline-block"/> {l}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            );
        };
        Component.displayName = 'JobsBlocker';
        return Component;
    }, []);

    const contextValue: NavigationProviderContextType = useMemo(() => ({
        navigateTo,
        customFlow,
        changeDocView,
        docSelectedListenersActive,
        setDocSelectedListenersActive,
        goBack,
        currentView,
        navLoading,
        setNavLoading,
        setDocumentAsync,
        setCurrentFolderAsync,
        setCurrentGroupAsync,
        getNavHeader,
        JobsBlocker,
        isDocExpanded,
        setIsDocExpanded,
    }), [
        navigateTo, customFlow, changeDocView,
        docSelectedListenersActive, currentView, navLoading,
        setDocumentAsync, setCurrentFolderAsync, setCurrentGroupAsync,
        getNavHeader, JobsBlocker, isDocExpanded,
    ]);

    return (
        <NavigationProviderContext.Provider value={contextValue}>
            {children}
        </NavigationProviderContext.Provider>
    );
};

export const useNavigationContext = () => useContext(NavigationProviderContext);
